import React from 'react';
import { ArrowRight, Sparkles, Flame, Clock, Beef, Zap, CheckCheck } from 'lucide-react';

export default function Hero({ t }) {
  const h = t.hero;

  return (
    <section className="relative min-h-screen flex items-center bg-ink bg-grid-dark overflow-hidden pt-24 pb-16">
      {/* Glow orbs */}
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-volt-400/10 blur-[140px] pointer-events-none" />
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-pulse-500/15 blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-volt-400/5 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-8 items-center">
          {/* Left: copy */}
          <div className="flex flex-col gap-7">
            <span className="inline-flex items-center gap-2 w-fit px-4 py-2 rounded-full border border-volt-400/30 bg-volt-400/10 text-volt-400 text-xs font-bold uppercase tracking-[0.18em]">
              <Sparkles size={14} />
              {h.badge}
            </span>

            <h1 className="font-display font-bold leading-[0.95] tracking-tight">
              <span className="block text-5xl sm:text-6xl lg:text-7xl text-frost">{h.line1}</span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl text-volt-400">{h.line2}</span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl text-outline">{h.line3}</span>
            </h1>

            <p className="text-lg text-mist max-w-xl leading-relaxed">{h.sub}</p>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="/s"
                className="inline-flex items-center gap-2 px-8 py-4 bg-volt-400 text-ink font-bold text-base rounded-full shadow-volt-glow hover:bg-volt-300 hover:shadow-volt-glow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                {h.cta}
                <ArrowRight size={18} strokeWidth={2.5} />
              </a>
              <a
                href="#platform"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-line text-frost font-semibold text-base hover:border-volt-400/60 hover:text-volt-400 transition-all duration-300"
              >
                {h.ctaSecondary}
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-4 max-w-md">
              {h.stats.map((s) => (
                <div key={s.label} className="flex flex-col gap-1 border-l-2 border-volt-400/40 pl-3">
                  <span className="font-display text-2xl sm:text-3xl font-bold text-frost">{s.value}</span>
                  <span className="text-xs text-mist leading-snug">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: phone mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Pulse ring behind phone */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-72 h-72 rounded-full border border-volt-400/30 animate-pulse-ring" />
              </div>

              {/* Phone */}
              <div className="relative w-[300px] sm:w-[330px] rounded-[2.8rem] border border-line bg-abyss shadow-card p-3 rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="rounded-[2.2rem] bg-surface overflow-hidden">
                  {/* Phone header */}
                  <div className="flex items-center gap-3 px-5 pt-6 pb-4 border-b border-line">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-volt-400 to-pulse-500 flex items-center justify-center">
                        <Zap size={18} className="text-ink" fill="currentColor" />
                      </div>
                      <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-volt-400 border-2 border-surface" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-frost">FitGainers Coach</p>
                      <p className="text-[11px] text-volt-400 font-medium">Online</p>
                    </div>
                  </div>

                  {/* Chat */}
                  <div className="flex flex-col gap-3 px-4 py-5 min-h-[300px]">
                    {h.chat.map((m, i) => (
                      <div
                        key={i}
                        className={`max-w-[85%] px-4 py-3 text-[13px] leading-snug rounded-2xl ${
                          m.from === 'user'
                            ? 'self-end bg-pulse-500 text-white rounded-br-md'
                            : 'self-start bg-raised text-frost rounded-bl-md border border-line'
                        }`}
                      >
                        {m.text}
                        {m.from === 'ai' && (
                          <span className="flex justify-end mt-1">
                            <CheckCheck size={13} className="text-volt-400" />
                          </span>
                        )}
                      </div>
                    ))}

                    {/* Generated recipe card */}
                    <div className="self-start w-[92%] rounded-2xl border border-volt-400/30 bg-ink/80 overflow-hidden">
                      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
                        <Sparkles size={13} className="text-volt-400" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-volt-400">
                          {h.recipeCard.tag}
                        </span>
                      </div>
                      <p className="px-4 text-sm font-bold text-frost">{h.recipeCard.title}</p>
                      <div className="flex items-center gap-3 px-4 py-3 text-[11px] text-mist font-medium">
                        <span className="inline-flex items-center gap-1">
                          <Flame size={12} className="text-volt-400" /> {h.recipeCard.kcal}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Beef size={12} className="text-volt-400" /> {h.recipeCard.protein}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock size={12} className="text-volt-400" /> {h.recipeCard.time}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating chips */}
              <div className="absolute -left-16 sm:-left-24 top-16 animate-float-slow">
                <div className="rounded-2xl border border-line bg-raised/90 backdrop-blur-md px-4 py-3 shadow-card">
                  <p className="text-[10px] uppercase tracking-widest text-mist font-semibold">
                    {h.floating[0].title}
                  </p>
                  <p className="text-sm font-bold text-volt-400">{h.floating[0].value}</p>
                </div>
              </div>
              <div className="absolute -right-8 sm:-right-16 bottom-20 animate-float-slower">
                <div className="rounded-2xl border border-line bg-raised/90 backdrop-blur-md px-4 py-3 shadow-card">
                  <p className="text-[10px] uppercase tracking-widest text-mist font-semibold">
                    {h.floating[1].title}
                  </p>
                  <p className="text-sm font-bold text-frost">{h.floating[1].value}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
